// ABOUTME: Date range picker with two side-by-side calendars in Ant Design style
// ABOUTME: Shows range highlighting between start and end dates with navigation arrows

import type { FC } from "react"
import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { format, addMonths, subMonths, isSameDay, isBefore, isAfter } from "date-fns"
import { useTranslation } from "react-i18next"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { CalendarMonthGrid } from "./CalendarMonthGrid"
import { useCalendarLabels } from "./useCalendarLabels"
import useDevice from "@shared/hooks/useDevice"
import { useZIndex } from "@shared/hooks/useZIndex"
import { CButton } from "./CButton"
import { CButtonToggleGroup } from "./CButtonToggleGroup"
import { DEFAULT_PRESETS, type DateRangePreset, type PresetConfig } from "./CDateRangePicker.presets"

export interface CDateRangePickerProps {
  startDate: Date | null
  endDate: Date | null
  onChange: (dates: [Date | null, Date | null]) => void
  onPresetSelect?: (preset: DateRangePreset) => void
  className?: string
  showPresets?: boolean
  presets?: PresetConfig[]
}

// Generate years from 2015 to current year + 1
const generateYears = (): number[] => {
  const currentYear = new Date().getFullYear()
  const years: number[] = []
  for (let year = 2015; year <= currentYear + 1; year++) {
    years.push(year)
  }
  return years
}

const YEARS = generateYears()

interface CalendarMonthProps {
  month: Date
  startDate: Date | null
  endDate: Date | null
  onDateClick: (date: Date) => void
  onDateHover: (date: Date | null) => void
  weekdays: string[]
  maxDate: Date
}

const CalendarMonth: FC<CalendarMonthProps> = ({
  month,
  startDate,
  endDate,
  onDateClick,
  onDateHover,
  weekdays,
  maxDate,
}) => (
  <CalendarMonthGrid
    month={month}
    weekdays={weekdays}
    dayState={(date) => ({
      disabled: isAfter(date, maxDate),
      // The two ends of the range are drawn as chosen.
      highlighted: Boolean(startDate && isSameDay(date, startDate)) || Boolean(endDate && isSameDay(date, endDate)),
    })}
    onDateClick={onDateClick}
    onDateHover={onDateHover}
  />
)

/** The month and year selects share one look: a borderless select with a small caret. */
const NAV_SELECT_CLASS =
  "text-sm font-medium bg-transparent border-none cursor-pointer focus:ring-0 p-0 pr-4 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2210%22%20height%3d%2210%22%20viewBox%3d%220%200%2012%2012%22%3e%3cpath%20fill%3d%22%236b7280%22%20d%3d%22M2%204l4%204%204-4%22%2f%3e%3c%2fsvg%3e')] bg-no-repeat bg-[position:right_0.25rem_center]"

interface MonthPaneProps extends Omit<CalendarMonthProps, "onDateHover"> {
  monthLabels: string[]
  /** Moves this pane to a month; the picker keeps the left pane before the right one. */
  onMove: (month: Date) => void
}

/** One month of the picker: the arrows and the month and year selects above the day grid. */
const MonthPane: FC<MonthPaneProps> = ({ month, monthLabels, onMove, ...calendar }) => (
  <div>
    <div className="flex items-center justify-between mb-3 px-1">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMove(subMonths(month, 1))}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>
        <select
          value={month.getMonth()}
          onChange={(e) => onMove(new Date(month.getFullYear(), parseInt(e.target.value, 10), 1))}
          className={NAV_SELECT_CLASS}
        >
          {monthLabels.map((label, index) => (
            <option
              key={index}
              value={index}
            >
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onMove(addMonths(month, 1))}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      <select
        value={month.getFullYear()}
        onChange={(e) => onMove(new Date(parseInt(e.target.value, 10), month.getMonth(), 1))}
        className={NAV_SELECT_CLASS}
      >
        {YEARS.map((year) => (
          <option
            key={year}
            value={year}
          >
            {year}
          </option>
        ))}
      </select>
    </div>
    <CalendarMonth
      month={month}
      onDateHover={() => {}}
      {...calendar}
    />
  </div>
)

export const CDateRangePicker: FC<CDateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  onPresetSelect,
  className = "",
  showPresets = true,
  presets = DEFAULT_PRESETS,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [isOpen, setIsOpen] = useState(false)
  const zIndex = useZIndex(isOpen)
  const [leftMonth, setLeftMonth] = useState<Date>(() => startDate ?? new Date())
  const [rightMonth, setRightMonth] = useState<Date>(() => {
    const start = startDate ?? new Date()
    return addMonths(start, 1)
  })
  const [selectingEnd, setSelectingEnd] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { weekdays, monthLabels } = useCalendarLabels()
  const maxDate = new Date(new Date().getFullYear() + 1, 11, 31)

  // Ensure left month is always <= right month
  const moveLeftMonth = (date: Date) => {
    const leftTime = new Date(date.getFullYear(), date.getMonth(), 1).getTime()
    const rightTime = new Date(rightMonth.getFullYear(), rightMonth.getMonth(), 1).getTime()

    if (leftTime >= rightTime) {
      // If left would be >= right, move right to left + 1 month
      setRightMonth(addMonths(date, 1))
    }
    setLeftMonth(date)
  }

  const moveRightMonth = (date: Date) => {
    const leftTime = new Date(leftMonth.getFullYear(), leftMonth.getMonth(), 1).getTime()
    const rightTime = new Date(date.getFullYear(), date.getMonth(), 1).getTime()

    if (rightTime <= leftTime) {
      // If right would be <= left, move left to right - 1 month
      setLeftMonth(subMonths(date, 1))
    }
    setRightMonth(date)
  }

  // Sync months with startDate when it changes externally — in the render that sees the change, so the panes
  // never paint a frame of the previous months.
  const [monthsSyncedFor, setMonthsSyncedFor] = useState({ startDate, isOpen, rightMonth })
  if (
    monthsSyncedFor.startDate !== startDate ||
    monthsSyncedFor.isOpen !== isOpen ||
    monthsSyncedFor.rightMonth !== rightMonth
  ) {
    setMonthsSyncedFor({ startDate, isOpen, rightMonth })
    if (startDate && !isOpen) {
      setLeftMonth(startDate)
      // Ensure right month is after left
      const leftTime = new Date(startDate.getFullYear(), startDate.getMonth(), 1).getTime()
      const rightTime = new Date(rightMonth.getFullYear(), rightMonth.getMonth(), 1).getTime()
      if (rightTime <= leftTime) {
        setRightMonth(addMonths(startDate, 1))
      }
    }
  }

  const handleDateClick = (date: Date) => {
    if (!selectingEnd || !startDate) {
      // First click - set start date
      onChange([date, null])
      setSelectingEnd(true)
    } else {
      // Second click - set end date
      if (isBefore(date, startDate)) {
        onChange([date, startDate])
      } else {
        onChange([startDate, date])
      }
      setSelectingEnd(false)
      setIsOpen(false)
    }
  }

  const handlePresetClick = (preset: PresetConfig) => {
    const { from, to } = preset.getRange()
    onChange([from, to])
    moveLeftMonth(from)
    onPresetSelect?.(preset.key)
    setSelectingEnd(false)
  }

  const formatDateRange = (): string => {
    if (!startDate) return t("datepicker.placeholder")
    const dateFormat = "dd.MM.yyyy"
    if (!endDate) return format(startDate, dateFormat)
    return `${format(startDate, dateFormat)} - ${format(endDate, dateFormat)}`
  }

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false)
        setSelectingEnd(false)
      }
    }
    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen])

  // Reset selection state when opening
  const handleOpen = () => {
    setIsOpen(true)
    if (startDate && endDate) {
      setSelectingEnd(false)
    }
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
    >
      <button
        type="button"
        onClick={handleOpen}
        className="flex items-center gap-2 px-3 py-1.5 h-[30px] text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full min-w-[205px]"
      >
        <CalendarIcon className="w-4 h-4 text-gray-500 shrink-0" />
        <span className="truncate">{formatDateRange()}</span>
      </button>

      {isOpen &&
        zIndex > 0 &&
        createPortal(
          <div
            // Not a control: with the mouse it repeats the closing Escape already does.
            role="presentation"
            className="fixed inset-0"
            style={{ zIndex }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setIsOpen(false)
                setSelectingEnd(false)
              }
            }}
          >
            <div
              className="fixed bg-white border border-gray-200 rounded-xl shadow-lg overflow-auto max-h-[90vh]"
              style={{
                zIndex: zIndex + 1,
                ...(isMobile
                  ? { top: 60, left: 8, right: 8 }
                  : {
                      top: containerRef.current ? containerRef.current.getBoundingClientRect().bottom + 4 : 100,
                      right: 16,
                    }),
              }}
            >
              <div className="p-4">
                {/* Presets */}
                {showPresets && (
                  <div className="mb-3 pb-3 border-b border-gray-200 flex flex-wrap items-center justify-center">
                    <CButtonToggleGroup>
                      {presets.map((preset) => (
                        <CButton
                          key={preset.key}
                          onClick={() => handlePresetClick(preset)}
                          size="xs"
                          color="gray"
                          label={t(preset.labelKey)}
                        />
                      ))}
                    </CButtonToggleGroup>
                  </div>
                )}

                {/* Calendars with navigation */}
                <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-4`}>
                  {/* Left calendar */}
                  <MonthPane
                    month={leftMonth}
                    onMove={moveLeftMonth}
                    monthLabels={monthLabels}
                    startDate={startDate}
                    endDate={endDate}
                    onDateClick={handleDateClick}
                    weekdays={weekdays}
                    maxDate={maxDate}
                  />

                  {/* Right calendar */}
                  <MonthPane
                    month={rightMonth}
                    onMove={moveRightMonth}
                    monthLabels={monthLabels}
                    startDate={startDate}
                    endDate={endDate}
                    onDateClick={handleDateClick}
                    weekdays={weekdays}
                    maxDate={maxDate}
                  />
                </div>

                {/* Today button at bottom */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date()
                      setLeftMonth(today)
                      setRightMonth(addMonths(today, 1))
                    }}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {t("calendar.today")}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
